import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ElementRef } from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';
import { DialogOverlay } from './dialog';

// Sheet e Dialog compartilham a primitiva do Radix: a semântica é a mesma (camada modal
// com foco preso), muda só de onde a superfície entra. O bottom sheet é o padrão mobile.
export const Sheet = SheetPrimitive.Root;
export const SheetTrigger = SheetPrimitive.Trigger;
export const SheetClose = SheetPrimitive.Close;

const sheetVariants = cva(
  cn(
    'fixed z-50 border-[color:var(--hairline)] bg-surface-1',
    'shadow-[var(--shadow-3)] duration-2 ease-soberano',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
  ),
  {
    variants: {
      side: {
        bottom: 'inset-x-0 bottom-0 rounded-t-xl border-t p-6 pb-9',
        top: 'inset-x-0 top-0 rounded-b-xl border-b p-6',
        left: 'inset-y-0 left-0 h-full w-3/4 max-w-sm border-r p-6',
        right: 'inset-y-0 right-0 h-full w-3/4 max-w-sm border-l p-6',
      },
    },
    defaultVariants: { side: 'bottom' },
  },
);

export type SheetContentProps = ComponentPropsWithoutRef<typeof SheetPrimitive.Content> &
  VariantProps<typeof sheetVariants>;

export const SheetContent = forwardRef<
  ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side, className, children, ...props }, ref) => (
  <SheetPrimitive.Portal>
    <DialogOverlay />
    <SheetPrimitive.Content ref={ref} className={cn(sheetVariants({ side }), className)} {...props}>
      {children}
    </SheetPrimitive.Content>
  </SheetPrimitive.Portal>
));

SheetContent.displayName = 'SheetContent';

export const SheetTitle = forwardRef<
  ElementRef<typeof SheetPrimitive.Title>,
  ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn('font-display text-[length:var(--d-4)] font-semibold text-ink-1', className)}
    {...props}
  />
));
SheetTitle.displayName = 'SheetTitle';
